import {useEffect, useState} from 'react';
import {Alert, ScrollView, View} from 'react-native';
import {Button, TextInput} from 'react-native-paper';
import {Text} from '../../Components';
import {PageContainer} from '../../Containers';
import {useTheme} from '../../Hooks';
import Decoders from '../../Models/decoders';
import {
  PROVIDER_ACTION_LABELS,
  ProviderActionType,
  ProviderAssessmentRecord,
  buildAssessmentTrend,
  buildProviderActionLabel,
  loadCompletedAssessments,
  saveProviderAction,
} from '../../Utils/Assessment';
import {shareProviderExport} from '../../Utils/Exports';
import {loadProviderSession} from '../../Utils/ProviderSession';
import {trackEvent} from '../../Utils/Telemetry';
import {RootProviderAssessmentScreenProps} from '../../Typings/NavigationTypes';
import getStyles from './ADAPT-MH.styles';

type Props = RootProviderAssessmentScreenProps;

const ProviderAssessment: React.FC<Props> = ({navigation, route}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [record, setRecord] = useState<ProviderAssessmentRecord | null>(null);
  const [records, setRecords] = useState<ProviderAssessmentRecord[]>([]);
  const [providerNotes, setProviderNotes] = useState('');
  const {fromScreening, sessionId} = route.params;

  useEffect(() => {
    const loadRecord = async () => {
      const nextRecords = await loadCompletedAssessments();
      const nextRecord = nextRecords.find((item) => item.sessionId === sessionId) || null;
      const canUseScreeningBypass = Boolean(fromScreening && nextRecord?.startedByProvider);

      if (!canUseScreeningBypass) {
        const session = await loadProviderSession();
        if (!session) {
          navigation.replace('ProviderLogin');
          return;
        }
      }

      setRecords(nextRecords);
      setRecord(nextRecord);
      setProviderNotes(nextRecord?.providerNotes || '');
    };

    loadRecord();
  }, [fromScreening, navigation, sessionId]);

  if (!record) {
    return (
      <PageContainer withSafeArea withPadding style={styles.page}>
        <View style={styles.card}>
          <Text weight="semiBold" style={styles.strongBody}>
            Assessment not found.
          </Text>
          <Button mode="contained" onPress={() => navigation.goBack()} style={styles.button}>
            Back
          </Button>
        </View>
      </PageContainer>
    );
  }

  const {resultSummary} = record;
  const isUrgent = resultSummary.riskFlag.triggered;
  if (record.sharingConsent === 'private') {
    return (
      <PageContainer withSafeArea withPadding style={styles.page}>
        <View style={styles.card}>
          <Text weight="bold" fontSize={18} style={styles.strongBody}>
            Summary is private.
          </Text>
          <Text style={styles.listItem}>
            This local demo assessment was marked private, so provider details are hidden.
          </Text>
          <Button mode="contained" onPress={() => navigation.goBack()} style={styles.button}>
            Back
          </Button>
        </View>
      </PageContainer>
    );
  }

  const relatedRecords = record.participantAlias
    ? records
        .filter((item) => item.participantAlias === record.participantAlias)
        .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
    : [record];
  const assessmentTrend = buildAssessmentTrend(record, relatedRecords);

  const onActionPress = async (actionType: ProviderActionType) => {
    const updatedRecord = await saveProviderAction(sessionId, actionType, providerNotes);
    if (updatedRecord) {
      setRecord(updatedRecord);
      setRecords(await loadCompletedAssessments());
      // Only the action type is shared; provider notes never leave the device.
      trackEvent('provider_action', {
        sessionId,
        language: updatedRecord.language,
        payload: {type: actionType},
      });
    }
  };

  const onExportPress = async (format: 'json' | 'csv') => {
    try {
      await shareProviderExport(record, format);
      trackEvent('export_shared', {sessionId, language: record.language, payload: {format}});
    } catch (error) {
      console.error('Failed to export provider summary', error);
      Alert.alert('Could not export summary', 'Please try again from this provider summary.');
    }
  };

  return (
    <PageContainer withSafeArea withPadding>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.hero}>
          <Text fontSize={28} lineHeight={35} weight="black" style={styles.title}>
            {resultSummary.headline}
          </Text>
          <Text fontSize={16} lineHeight={24} style={styles.subtitle}>
            Completed {new Date(resultSummary.completedAt).toLocaleString()}
          </Text>
        </View>

        <View style={[styles.card, isUrgent ? styles.urgentCard : null]}>
          <Text weight="bold" fontSize={17} style={styles.strongBody}>
            Recommended action
          </Text>
          <Text style={styles.listItem}>{buildProviderActionLabel(resultSummary)}</Text>
          <Text style={styles.listItem}>
            Risk flag: {isUrgent ? resultSummary.riskFlag.message : 'No urgent flag triggered.'}
          </Text>
        </View>

        <View style={styles.card}>
          <Text weight="bold" fontSize={17} style={styles.strongBody}>
            Follow-up monitoring
          </Text>
          <View
            style={[
              styles.trendBadge,
              assessmentTrend.direction === 'higher' ? styles.trendBadgeWarning : null,
              assessmentTrend.direction === 'lower' ? styles.trendBadgePositive : null,
            ]}
          >
            <Text weight="semiBold" style={styles.trendBadgeText}>
              {assessmentTrend.label}
            </Text>
          </View>
          <Text style={styles.listItem}>{assessmentTrend.description}</Text>
          {assessmentTrend.deterioration ? (
            <Text style={styles.warningText}>
              Deterioration flag: review whether support should be stepped up.
            </Text>
          ) : null}
        </View>

        <View style={styles.card}>
          <Text weight="bold" fontSize={17} style={styles.strongBody}>
            Provider action
          </Text>
          <TextInput
            mode="outlined"
            label="Provider notes"
            value={providerNotes}
            onChangeText={setProviderNotes}
            multiline
            style={styles.input}
          />
          {(Object.keys(PROVIDER_ACTION_LABELS) as ProviderActionType[]).map((actionType) => (
            <Button
              key={actionType}
              mode={record.providerAction?.type === actionType ? 'contained' : 'outlined'}
              onPress={() => onActionPress(actionType)}
              style={styles.secondaryButton}
              buttonColor={
                record.providerAction?.type === actionType ? theme.colors.primary : undefined
              }
              textColor={
                record.providerAction?.type === actionType
                  ? theme.colors.textContrast
                  : theme.colors.primary
              }
            >
              {PROVIDER_ACTION_LABELS[actionType]}
            </Button>
          ))}
          {record.providerAction ? (
            <Text style={styles.footerNote}>
              Last action: {record.providerAction.label} at{' '}
              {new Date(record.providerAction.actedAt).toLocaleString()}
            </Text>
          ) : null}
        </View>

        <View style={styles.card}>
          <Text weight="bold" fontSize={17} style={styles.strongBody}>
            Export
          </Text>
          <Text style={styles.listItem}>
            Provider exports include probabilities and local action notes. EHR integration is
            deferred.
          </Text>
          <Button
            mode="outlined"
            onPress={() => onExportPress('json')}
            style={styles.secondaryButton}
            textColor={theme.colors.primary}
          >
            Export JSON
          </Button>
          <Button
            mode="outlined"
            onPress={() => onExportPress('csv')}
            style={styles.secondaryButton}
            textColor={theme.colors.primary}
          >
            Export CSV
          </Button>
        </View>

        <View style={styles.card}>
          <Text weight="bold" fontSize={17} style={styles.strongBody}>
            Model probabilities
          </Text>
          {Object.entries(resultSummary.predictions).map(([key, value]) => {
            const label = Decoders[key as keyof typeof Decoders] || key;
            const fillWidth = `${Math.max(0, Math.min(100, value * 100))}%`;

            return (
              <View key={key} style={styles.probabilityRow}>
                <View style={styles.row}>
                  <Text style={styles.strongBody}>{label}</Text>
                  <Text weight="bold" style={styles.strongBody}>
                    {(value * 100).toFixed(0)}%
                  </Text>
                </View>
                <View style={styles.probabilityTrack}>
                  <View style={[styles.probabilityFill, {width: fillWidth}]} />
                </View>
              </View>
            );
          })}
        </View>

        <View style={styles.card}>
          <Text weight="bold" fontSize={17} style={styles.strongBody}>
            Assessment details
          </Text>
          <Text style={styles.listItem}>Mode: {record.mode}</Text>
          <Text style={styles.listItem}>Language: {record.language}</Text>
          <Text style={styles.listItem}>
            Sharing consent: {record.sharingConsent || 'not specified'}
          </Text>
          <Text style={styles.listItem}>
            Participant: {record.participantAlias || 'Demo participant'}
          </Text>
          <Text style={styles.listItem}>Questions answered: {record.answers.length}</Text>
          <Text style={styles.listItem}>
            Top concern: {resultSummary.topConcernLabel || 'Not available'}
          </Text>
        </View>

        <View style={styles.card}>
          <Text weight="bold" fontSize={17} style={styles.strongBody}>
            Follow-up timeline
          </Text>
          {relatedRecords.map((item) => {
            const itemTrend = buildAssessmentTrend(item, relatedRecords);

            return (
              <View key={item.sessionId} style={styles.timelineItem}>
                <Text weight="semiBold" style={styles.strongBody}>
                  {new Date(item.resultSummary.completedAt).toLocaleString()}
                </Text>
                <Text style={styles.listItem}>{item.resultSummary.headline}</Text>
                <Text style={styles.listItem}>{itemTrend.label}</Text>
                {itemTrend.deterioration ? (
                  <Text style={styles.warningText}>Deterioration flag</Text>
                ) : null}
                <Text style={styles.listItem}>
                  Action:{' '}
                  {item.providerAction?.label || buildProviderActionLabel(item.resultSummary)}
                </Text>
              </View>
            );
          })}
        </View>

        {fromScreening ? (
          <Button
            mode="outlined"
            onPress={() => navigation.navigate('Landing')}
            style={styles.secondaryButton}
            textColor={theme.colors.primary}
          >
            Back to home
          </Button>
        ) : null}
      </ScrollView>
    </PageContainer>
  );
};

export default ProviderAssessment;
